import { useTranslation } from 'react-i18next';
import { FormatUtils, ImageUtils, PaletteUtils, cn } from '@shared/utils';
import { CardKind, CardStatus, type CardResponse } from '@shared/api';
import { Icon, Text } from '@shared/widgets';
import EllipseIcon from '@assets/icons/card/ellipse.svg';
import PinstripeIcon from '@assets/icons/card/pinstripe.svg';
import VisaIcon from '@assets/icons/card/visa.svg';
import MastercardIcon from '@assets/icons/card/mastercard.svg';
import { useCardFlow } from '@shared/hooks';

interface CardItemProps {
  className?: string;
  withHover?: boolean;
  card: CardResponse;
}

export const CardItem = (props: CardItemProps) => {
  const { t } = useTranslation('accounts');

  const closed = props.card.status === CardStatus.Closed;
  const blocked = props.card.status === CardStatus.Blocked || props.card.status === CardStatus.Frozen;
  const disabled = props.card.status === CardStatus.Delinquent || props.card.status === CardStatus.Inactive;
  const cardFlow = useCardFlow(props.card.cardId);
  return (
    <div
      className={cn(
        'group relative shrink-0',
        props.withHover && 'cursor-pointer transition-transform duration-200 hover:scale-110',
        cardFlow?.viewCardDetails === false && 'cursor-not-allowed hover:scale-100',
        props.className,
      )}
      title={cardFlow?.viewCardDetails === false ? t('card.viewCardDetails') : undefined}
    >
      <div
        className={cn(
          'relative flex h-[103px] w-[176px] flex-col justify-between overflow-hidden rounded-lg border border-[#3d3d3d] bg-soft-black bg-cover bg-center bg-no-repeat p-3 pt-5 text-white',
          closed && 'border-[#5c5c5c] bg-slate',
          blocked && 'border-red',
          disabled && 'border-[#bebebe] bg-[#b7b7b7]',
          PaletteUtils.isVcfcPalette && 'bg-primary text-ice',
          PaletteUtils.isVcfcPalette && [
            (props.card.status === CardStatus.Blocked || props.card.status === CardStatus.Delinquent) && 'bg-soft-black',
            closed && 'bg-slate',
            props.card.status === CardStatus.Inactive && '!bg-light-gray',
          ],
        )}
        style={{ backgroundImage: `url(${ImageUtils.getCloudinaryImage(props.card.cardDesignImageUrl)})` }}
      >
        {!props.card.cardDesignImageUrl && (
          <>
            {PaletteUtils.isInsigniaPalette && (
              <>
                <Icon
                  icon={
                    <EllipseIcon
                      className={cn('absolute right-[-16px] top-[-36px] fill-white opacity-[0.02]', (disabled || closed) && 'fill-black')}
                    />
                  }
                />
                <Icon
                  icon={
                    <EllipseIcon
                      className={cn('absolute bottom-[-38px] left-[-36px] fill-white opacity-[0.02]', (disabled || closed) && 'fill-black')}
                    />
                  }
                />
              </>
            )}
            {PaletteUtils.isVcfcPalette && <Icon className='absolute inset-0' icon={<PinstripeIcon />} />}
          </>
        )}
        <ul className='flex items-center'>
          <Text
            className={cn(
              'text-[8px] tracking-[0.03em]',
              'after:relative after:top-[-2px] after:mx-1 after:inline-block after:h-[2px] after:w-[2px] after:rounded-full after:bg-current',
              disabled && ['text-ice', 'after:bg-ice'],
            )}
            tag='li'
            uppercase
            weight='medium'
            color='white'
          >
            {t(`card.type.${props.card.cardType.toLowerCase()}`)}
          </Text>
          <Text
            className={cn('text-[8px] tracking-[0.03em]', disabled && 'text-ice', blocked && 'text-red')}
            tag='li'
            uppercase
            weight='medium'
            color='white'
          >
            {t(`statuses.card.${props.card.status}`, { ns: 'common' })}
          </Text>
        </ul>
        <div className='flex items-end justify-between'>
          <ul className='flex max-w-[calc(100%-47px)] flex-col gap-1'>
            <Text className={cn('tracking-[0.03em]', disabled && 'text-ice')} tag='li' truncate weight='medium' size='3xs' color='white'>
              {FormatUtils.toSliceNumber(props.card.cardNumber, 16)}
            </Text>
            <Text tag='li' truncate weight='medium' size='xxs' color='white'>
              {props.card.cardHolder}
            </Text>
          </ul>
          {props.card.cardKind === CardKind.Credit && !props.card.cardDesignImageUrl && (
            <Icon className='shrink-0' icon={<VisaIcon />} color='white' />
          )}
          {props.card.cardKind === CardKind.Debit && !props.card.cardDesignImageUrl && (
            <Icon className='shrink-0' icon={<MastercardIcon className='h-[27.5px] w-full' />} />
          )}
        </div>
      </div>
      {props.withHover && cardFlow?.viewCardDetails !== false && (
        <Text
          className={cn(
            'absolute left-1/2 top-full -translate-x-1/2 whitespace-nowrap text-center opacity-0 transition-opacity duration-200',
            'group-hover:opacity-100',
          )}
          size='sm'
        >
          Click to view details
        </Text>
      )}
    </div>
  );
};
